<template>
  <cf-validated-form
    name="menuItem"
    :disabled="order.isAddingItem"
    @dirty="dirtyFormHandler"
    @valid-submit="submitHandler"
  >
    <template v-if="selectedItem">
      <div :class="['cf-flex cf-flex--column', { 'cf-margin-top-16': images.length > 0 }]">
        <cf-image-scroller
          v-if="images.length > 0"
          :images="images"
        />

        <div class="cf-flex cf-flex--column cf-flex--gap-20 cf-text-align-left cf-modal__subheader cf-modal__subheader--sticky">
          <div class="cf-flex cf-flex--column cf-flex--gap-8">
            <div
              v-if="areBannersDisplayed || showFavoriteButton"
              class="cf-flex cf-flex--row cf-flex--justify-space-between"
            >
              <!-- Favorite Button -->
              <div
                v-if="showFavoriteButton"
                class="cf-flex cf-flew--row cf-flex--justify-end"
              >
                <cf-favorite-button
                  ref="favoriteButton"
                  class="icon--bigger"
                  :item="selectedItem"
                  :modifiers="modifiers"
                  :special-instructions="specialInstructions"
                  :validate-form="true"
                />
              </div>

              <!-- Banners -->
              <div class="cf-flex cf-flew--row cf-flex--justify-end cf-flex--gap-8">
                <!-- New Banner -->
                <div
                  v-if="selectedItem.isNewItem"
                  class="cf-new-item-banner"
                >
                  New!
                </div>

                <!-- Low Stock Banner -->
                <div
                  v-if="selectedItem.isLowInventory"
                  class="cf-low-inventory-banner"
                >
                  <div class="cf-low-inventory-content">
                    Low Stock
                  </div>

                  <div
                    v-if="isAtLowStockThreshold"
                    class="cf-low-inventory-amount"
                  >
                    {{ remainingInventory }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Item Name -->
            <h2 class="cf-title cf-title--2">
              {{ selectedItem.displayName }}
            </h2>

            <!-- Item Rating -->
            <div
              v-if="isItemRatingDisplayed"
              class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-4"
            >
              <span class="cf-subtitle cf-subtitle--2">
                {{ itemRating }}
              </span>

              <span class="cf-icon cf-icon--smaller cf-icon--red fa fa-star" />

              <span
                v-if="numberOfRatings"
                class="cf-subtitle cf-subtitle--2"
              >
                {{ formattedNumberOfRatings }}
              </span>
            </div>

            <!-- Item Calories -->
            <h3
              v-if="calorieDisplay"
              class="cf-title cf-title--3"
            >
              {{ calorieDisplay }}
            </h3>

            <cf-current-day-menu-schedule :schedule="selectedItem.schedule" />

            <!-- Item Description -->
            <v-clamp
              v-if="selectedItem.descriptionText"
              tag="p"
              class="pre-line cf-subtitle cf-subtitle--2 cf-subtitle--black"
              autoresize
              :max-lines="10"
            >
              <template #default>
                {{ selectedItem.descriptionText }}
              </template>

              <template #after="{ clamped, expanded, toggle }">
                <template v-if="clamped || expanded">
                  <br>

                  <button
                    class="cf-link cf-link--brand-secondary cf-link--heavy"
                    type="button"
                    @click="toggle"
                  >
                    {{ clamped ? 'Read More' : 'Read Less' }}
                  </button>
                </template>
              </template>
            </v-clamp>
          </div>

          <!-- Dietary Attributes -->
          <div
            v-if="dietaryAttributes.length > 0"
            class="cf-flex cf-flex--row cf-flex--gap-16"
          >
            <div
              v-if="dietaryAttributes.length > 0"
              class="cf-flex cf-flex--row cf-flex--wrap cf-flex--gap-16"
            >
              <div
                v-for="attribute in dietaryAttributes"
                :key="attribute.name"
                class="cf-flex cf-flex--row cf-flex--wrap cf-flex--align-center cf-flex--gap-8"
              >
                <img
                  class="cf-image-icon cf-image--24"
                  :src="attribute.imageUrl"
                  :alt="attribute.name"
                >

                <p class="cf-subtitle cf-subtitle--2">
                  {{ attribute.name }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Modifier Groups -->
        <div
          v-if="selectedItem.modifierGroups.length > 0"
          class="cf-flex cf-flex--column"
        >
          <cf-modifier-groups
            :groups="selectedItem.modifierGroups"
            :dynamic-groups="itemDynamicGroups"
            :read-only="isReadOnly || !selectedLocation.storeId || !selectedLocation.isReadyForOrdering || order.groupOrder.isLocked || menu.unavailablePerSchedule"
            :hide-prices="selectedLocation.features.HidePrices"
            :selected-modifiers="selectedModifiers"
            @selected="modifiersSelectedHandler"
            @set-dynamic-group-modifier-source="setDynamicGroupModifierSourceHandler"
          />
        </div>

        <!-- Item Specific Instructions -->
        <div
          v-if="merchant.options.itemLevelInstructionsEnabled && selectedLocation.storeId"
          class="cf-flex cf-flex--column cf-flex--gap-10 cf-text-align-left cf-modal-overflow cf-border-top-thick"
        >
          <cf-text-input
            v-model="specialInstructions"
            type="text"
            :label="orderItemInstructionsPlaceholderContent"
            name="specialInstructions"
            :validations="specialInstructionsValidations"
          />

          <p class="cf-subtitle cf-subtitle--3">
            {{ content.ItemLevelInstructions.metadata.inputSubtitle }}
          </p>
        </div>

        <div
          v-if="!isReadOnly && selectedItem.maxAllowed !== 0"
          class="cf-modal__footer cf-modal__footer--row cf-modal__footer--sticky"
        >
          <group-order-locked-message v-if="order.groupOrder.isLocked" />

          <p
            v-else-if="!selectedLocation.isCurrentServiceAvailable"
            class="cf-subtitle cf-subtitle--3"
          >
            Ordering is unavailable at this time.
          </p>

          <!-- Select Location Cta -->
          <div
            v-else-if="!selectedLocation.storeId"
            class="cf-flex cf-flex--row cf-flex--gap-12 cf-full-width"
          >
            <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--wrap">
              <p class="cf-subtitle cf-subtitle--3">
                Select a location in order to add items to cart.
              </p>
            </div>

            <button
              class="cf-button cf-button--lg cf-button--secondary cf-button--auto"
              type="button"
              @click="$openModal('editOrderDetails')"
            >
              Select Location
            </button>
          </div>

          <p
            v-else-if="!selectedLocation.isReadyForOrdering"
            class="cf-subtitle cf-subtitle--3"
          >
            <template v-if="selectedLocation.outsideGeoFence">
              This store is too far away and can’t accept orders at this time
            </template>

            <template v-else>
              This store is currently closed and can’t accept orders at this time.
            </template>
          </p>

          <template v-else-if="menu.unavailablePerSchedule">
            <p class="cf-subtitle cf-subtitle--3">
              This menu is currently unavailable for ordering. Please check back later.
            </p>
          </template>

          <!-- Qty input and Add/Update Cta -->
          <template v-else>
            <cf-quantity-input
              v-model="quantity"
              :max-value="maxAllowed"
              name="menuItemQuantity"
            />

            <button
              :class="['cf-button cf-button--primary cf-button--lg cf-button--auto', { 'cf-button--loading': order.isAddingItem }]"
              :disabled="!currentQuantityIsValid"
              type="submit"
            >
              <template v-if="selectedOrderItem">
                Update
              </template>

              <template v-else>
                Add
              </template>

              <template v-if="selectedLocation.storeId && !selectedLocation.features.HidePrices">
                {{ $dollars(priceWithModifiers) }}
              </template>
            </button>

            <template v-if="!currentQuantityIsValid">
              <hr class="cf-hr">

              <p class="cf-subtitle cf-subtitle--3">
                <span
                  v-if="orderValidations.warnings.orderMaximumItemCount"
                  v-html="orderValidations.warnings.orderMaximumItemCount"
                />

                <template v-else>
                  Your cart has reached the limit for this item <b>({{ selectedItem.maxAllowed }})</b>
                </template>
              </p>
            </template>
          </template>
        </div>
      </div>
    </template>

    <!-- Item not available Cta -->
    <div
      v-else
      class="cf-flex cf-flex--column cf-flex--gap-16"
    >
      <h2 class="cf-title cf-title--2">
        Item Not Available
      </h2>

      <p class="cf-subtitle cf-subtitle--2">
        We're sorry, this menu item is not available at your selected location.
      </p>

      <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-bottom-16">
        <button
          type="button"
          class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
          @click="$openModal('editOrderDetails')"
        >
          Change Location
        </button>

        <button
          type="button"
          class="cf-button cf-button--primary cf-button--lg cf-button--fill"
          @click="$closeModal('menuItem')"
        >
          Browse Menu
        </button>
      </div>
    </div>
  </cf-validated-form>
</template>


<script>
  /* eslint-disable prefer-destructuring */
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';

  import calculatePriceWithModifiers from '@/helpers/calculate-price-with-modifiers';

  import kebabcase from '@/filters/kebabcase';

  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfQuantityInput from '@/view/components/shared/cf-quantity-input/index.vue';
  import CfCurrentDayMenuSchedule from '@/view/components/globals/cf-current-day-menu-schedule/index.vue';
  import CfFavoriteButton from '@/view/components/shared/cf-favorite-button/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';

  import CfImageScroller from '@/view/components/globals/cf-image-scroller/index.vue';


  export default {
    name: 'CfMenuItem',

    components: {
      CfValidatedForm,
      CfTextInput,
      CfQuantityInput,
      CfCurrentDayMenuSchedule,
      CfFavoriteButton,
      CfImageScroller
    },

    props: {
      isReadOnly: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        modifiers: [],
        specialInstructions: null,
        dynamicGroupModifierSource: null,
        quantity: 1
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('menu', ['menu', 'selectedCategory', 'selectedItem', 'selectedFavoriteItem', 'selectedOrderItem']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order', 'orderValidations', 'orderItemQuantitiesPerMenuItem']),
      ...mapGetters('session', ['orderDetails', 'session']),


      areBannersDisplayed() {
        return this.selectedItem.isNewItem || this.selectedItem.isLowInventory || this.isAtLowStockThreshold;
      },

      isAtLowStockThreshold() {
        return !!this.selectedItem.inventoryCount;
      },

      itemRating() {
        return this.selectedItem.averageRating?.toFixed(2);
      },

      numberOfRatings() {
        return this.selectedItem.numberOfRatings;
      },

      formattedNumberOfRatings() {
        function _formatRating(numberOfRatings) {
          switch (true) {
            case numberOfRatings >= 1000000:
              return `(${Math.round(numberOfRatings / 1000000)}M+)`;
            case numberOfRatings >= 1000:
              return `(${Math.round(numberOfRatings / 1000)}K+)`;
            default:
              return `(${numberOfRatings})`;
          }
        }

        const formattedRating = _formatRating(this.numberOfRatings);

        return formattedRating;
      },

      isItemRatingDisplayed() {
        const isMerchantMenuItemRatingEnabled = this.merchant.features['item-rating-enabled'];

        const hasItemRatingMetReviewThreshold = this.itemRating && this.itemRating >= this.merchant.options.menuRatingThreshold;

        return isMerchantMenuItemRatingEnabled && hasItemRatingMetReviewThreshold;
      },

      remainingInventory() {
        return `${this.selectedItem.inventoryCount} remaining`;
      },

      calorieDisplay() {
        return this.selectedItem.nutritionalInfo?.calories;
      },

      currentQuantityIsValid() {
        const { orderItemCount, orderItemStagedCount } = this.order;

        const orderMaximumItemCount = this.orderValidations.validations && this.orderValidations.validations.orderMaximumItemCount;

        let isValid = this.selectedItem.maxAllowed === -1 || this.existingQuantity + this.quantity <= this.selectedItem.maxAllowed;

        if (isValid && orderMaximumItemCount) {
          isValid = this.selectedOrderItem
            ? (orderItemCount + orderItemStagedCount) - this.selectedOrderItem.quantity + this.quantity <= orderMaximumItemCount
            : (orderItemCount + orderItemStagedCount) + this.quantity <= orderMaximumItemCount;
        }

        return isValid;
      },

      existingQuantity() {
        let quantity = this.orderItemQuantitiesPerMenuItem[this.selectedItem.menuItemId] || 0;

        if (this.selectedOrderItem) {
          quantity -= this.selectedOrderItem.quantity;
        }

        return quantity;
      },

      dietaryAttributes() {
        return this.selectedItem.attributes
          ? this.selectedItem.attributes
            .filter(attribute => ['Allergen', 'FoodType', 'SpiceLevel'].includes(attribute.type))
            .map(attribute => ({ ...attribute, iconName: kebabcase(attribute.name) }))
          : [];
      },

      images() {
        const images = [];

        if (this.selectedItem.images) {
          this.selectedItem.images.forEach((image) => {
            if (image.label && image.label.indexOf('itemDetails') === 0) {
              images.push({
                altText: this.selectedItem.displayName,
                source: image.url
              });
            }
          });
        }

        return images;
      },

      itemDynamicGroups() {
        return this.selectedItem.itemGroups;
      },

      maxAllowed() {
        const isUnlimited = this.selectedItem.maxAllowed === -1;

        let itemLimit = this.selectedItem.maxAllowed;

        if (!isUnlimited) itemLimit = Math.max((this.selectedItem.maxAllowed - this.existingQuantity), 0);

        const { orderMaximumItemCount } = this.orderValidations.validations;

        if (orderMaximumItemCount) {
          let cartLimit = Math.max(orderMaximumItemCount - (this.order.orderItemCount + this.order.orderItemStagedCount), 0);

          if (this.selectedOrderItem) cartLimit += this.selectedOrderItem.quantity;

          return isUnlimited ? cartLimit : Math.min(itemLimit, cartLimit);
        }

        return itemLimit;
      },

      priceWithModifiers() {
        return calculatePriceWithModifiers(this.selectedItem, this.modifiers) * this.quantity;
      },

      selectedModifiers() {
        if (this.selectedOrderItem) {
          return this.selectedOrderItem.modifiers;
        }

        if (this.selectedFavoriteItem) {
          return this.selectedFavoriteItem.modifiers;
        }

        return null;
      },

      itemProductTags() {
        return this.selectedItem.attributes
          .filter(attribute => attribute.type === 'ProductTag')
          .map(attribute => ({ ...attribute, class: `tag--${kebabcase(attribute.name)}` }));
      },

      showFavoriteButton() {
        return this.selectedLocation.storeId
          && this.selectedLocation.isReadyForOrdering
          && !this.menu.unavailablePerSchedule
          && this.selectedLocation.isCurrentServiceAvailable;
      },

      orderItemInstructionsPlaceholderContent() {
        return this.content.ItemLevelInstructions.metadata.inputPlaceholder;
      },

      specialInstructionsValidations() {
        const maxLength = this.selectedLocation.pos.maxCharacterLimitForItemLevelInstructions;

        if (maxLength && maxLength > 0) {
          return { max: maxLength };
        }

        return {};
      },

      isRedirectToOrderAtTableSummary() {
        const isOrderAtTable = this.orderDetails.orderType === 'table';

        const isCheckoutParam = this.session.route.action === 'checkout';

        return isOrderAtTable && isCheckoutParam;
      }
    },

    watch: {
      'selectedItem.menuItemId': 'resetHandler'
    },

    created() {
      this.onComponentCreated();
    },

    methods: {
      onComponentCreated() {
        if (this.selectedOrderItem) {
          this.specialInstructions = this.selectedOrderItem.specialInstructions;

          this.quantity = this.selectedOrderItem.quantity || 1;
        }

        else if (this.selectedFavoriteItem) {
          this.specialInstructions = this.selectedFavoriteItem.specialInstructions;
        }
      },

      dirtyFormHandler() {
        this.$refs.favoriteButton.resetIsFavorite();
      },

      modifiersSelectedHandler(modifiers) {
        this.modifiers = modifiers;
      },

      setDynamicGroupModifierSourceHandler(dynamicGroupModifierSource) {
        this.dynamicGroupModifierSource = dynamicGroupModifierSource;
      },

      parseDynamicModifiers() {
        return this.modifiers.filter(mod => mod.menuItemId !== this.dynamicGroupModifierSource.menuItemId);
      },

      async submitHandler() {
        let menuItemId = this.selectedItem.menuItemId;

        let modifiers = this.modifiers;

        if (this.dynamicGroupModifierSource) {
          menuItemId = this.dynamicGroupModifierSource.menuItemGroupItemId;

          modifiers = this.parseDynamicModifiers();
        }

        try {
          if (this.selectedOrderItem) {
            await this.editItem(menuItemId, modifiers);
          }
          else {
            await this.addItem(menuItemId, modifiers);
          }

          this.$emit('reset-handler');
        }
        catch (error) {
          errorHandlers.silent(error);
        }
      },

      async editItem(menuItemId, modifiers) {
        try {
          const itemPayload = {
            menuItemId,
            modifiers,
            favoriteItemId: this.selectedItem.favoriteItemId,
            orderItemId: this.selectedOrderItem.orderItemId,
            specialInstructions: this.specialInstructions,
            quantity: this.quantity,
            categoryId: this.selectedCategory?.categoryId
          };

          await this.$store.dispatch('orders/editItem', { item: itemPayload });
        }
        catch (error) {
          throw error;
        }
      },

      async addItem(menuItemId, modifiers) {
        try {
          /**
            * there may not be a selected category if the item was added from menu search
            * where search results list items without context of the categories they belong to
          */
          const itemPayload = {
            menuItemId,
            modifiers,
            displayName: this.selectedItem.displayName,
            isUpsell: this.selectedItem.isUpsell,
            maxAllowed: this.selectedItem.maxAllowed,
            specialInstructions: this.specialInstructions,
            quantity: this.quantity,
            price: this.selectedItem.price,
            categoryId: this.selectedCategory?.categoryId,
            favoriteItemId: this.selectedItem.favoriteItemId
          };

          await this.$store.dispatch('orders/addItem', { item: itemPayload });

          if (this.isRedirectToOrderAtTableSummary) {
            this.$store.dispatch('session/updateRouteParamsDirectly', { action: null });

            const routerPayload = {
              name: 'orderAtTableCartSummaryPage',
              params: { merchantId: this.$route.params.merchantId }
            };

            this.$router.push(routerPayload);
          }

          // this.$triggerGoogleTrackingEvent('AddItemToCart', 'Order');

          this.$triggerNewRelicEvent('AddItemToCart', 'Information', 'Added Item To Cart');
        }
        catch (error) {
          throw error;
        }
      },

      resetHandler() {
        this.modifiers = [];

        this.specialInstructions = null;

        this.quantity = 1;
      }
    }
  };
</script>
